import { z } from 'zod';
/**
 * Onboarding wizard · Step 3 (voice / cadence / autonomy).
 *
 * Captures the three chip-pick choices the operator makes in
 * `Onboarding · Step 3` of the redesigned wizard. Source-of-truth
 * design lives in
 * `tmp/onboarding-mock/bundle/leanopx/project/assets/90d2136d-…js`
 * (lines 420–455). The mock uses human-friendly chip labels; the wire
 * stores kebab-case slugs (`daily-8am`, `draft-only`, …) and the
 * client renders the matching `*_LABEL` strings.
 *
 * Existing shared types we deliberately do NOT reuse here:
 *   - `BriefingCadence`     (user.ts)            — different slug set
 *                                                  (`weekdays`/`on-change`),
 *                                                  captured for the
 *                                                  personal-profile
 *                                                  page, not the org
 *                                                  onboarding wizard.
 *   - `AgentAutonomyLevel`  (user.ts)            — three-level personal
 *                                                  autonomy, different
 *                                                  cardinality from the
 *                                                  wizard's four-level
 *                                                  choice.
 *   - `AgentVoice` /                              — values match the
 *     `AgentVoiceCanonical`  (settings/…) ──┐      mock 1:1; we mirror
 *                                            │      the lowercase canonical
 *                                            └──→  set here for
 *                                                  symmetry with the
 *                                                  Cadence + Autonomy
 *                                                  enums in this file.
 *                                                  Server-side adapters
 *                                                  can map this slug
 *                                                  onto `AgentVoice`
 *                                                  ('Coach' / …) when
 *                                                  persisting to the
 *                                                  org-wide
 *                                                  AgentSettings.
 *
 * The wizard writes `OnboardingVoicePreferences` into the org's
 * onboarding state machine; downstream the values are projected onto
 * the org's `AgentSettings` (voice) + per-user
 * `UserAgentPreferences` (cadence + autonomy) so the existing surfaces
 * keep working without a second source of truth.
 */
export const OnboardingVoiceTone = z.enum(['coach', 'analyst', 'operator', 'quiet']);
/** Human label per tone (the chip text in the mock). */
export const ONBOARDING_VOICE_TONE_LABEL = {
    coach: 'coach',
    analyst: 'analyst',
    operator: 'operator',
    quiet: 'quiet',
};
export const OnboardingBriefingCadence = z.enum([
    'daily-8am',
    'weekly',
    'daily-plus-monday',
    'on-change-only',
]);
export const ONBOARDING_BRIEFING_CADENCE_LABEL = {
    'daily-8am': 'Daily 8am',
    weekly: 'Weekly',
    'daily-plus-monday': 'Daily + Monday recap',
    'on-change-only': 'Only when something changes',
};
export const OnboardingAutonomyLevel = z.enum([
    'draft-only',
    'send-after-approval',
    'send-routine',
    'hands-off',
]);
export const ONBOARDING_AUTONOMY_LEVEL_LABEL = {
    'draft-only': 'Draft, never send',
    'send-after-approval': 'Send after approval',
    'send-routine': 'Send routine, escalate exceptions',
    'hands-off': 'Hands-off',
};
/**
 * Persisted record for an org's onboarding voice/cadence/autonomy
 * picks. `orgId` is denormalized into the doc body so a collection-group
 * query can scope by tenant without joining the doc path.
 */
export const OnboardingVoicePreferences = z.object({
    orgId: z.string().min(1),
    tone: OnboardingVoiceTone,
    cadence: OnboardingBriefingCadence,
    autonomy: OnboardingAutonomyLevel,
    /** ISO-8601 timestamp of the last server-side write. */
    updatedAt: z.string().datetime(),
    /** Firebase uid of the writer. */
    updatedBy: z.string().min(1),
});
/**
 * Body of `PUT /api/orgs/:o/onboarding/voice`. The handler
 * derives `orgId` from the route param + `updatedAt`/`updatedBy` from
 * the request context — clients only send the three picks.
 */
export const UpdateOnboardingVoicePreferencesRequest = z.object({
    tone: OnboardingVoiceTone,
    cadence: OnboardingBriefingCadence,
    autonomy: OnboardingAutonomyLevel,
});
